import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshToken = useCallback(async () => {
  console.log(`[${new Date().toISOString()}] 🔄 Attempting token refresh...`);

  try {
    const response = await fetch('https://n8n.iniai.cloud/webhook/refresh-token-2', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
        'Content-Type': 'application/json'
      },
      body: '{}'
    });

    const data = await response.json();
    console.log('✅ Refresh successful:', data);
    
    if (data[0]?.success === true) {
      // ... rest of your existing code
    }
  } catch (error) {
    console.error('❌ Token refresh failed:', error);
    logout();
    return { success: false };
  }
}, []);

  const verifyToken = useCallback(async () => {
    console.log('🔍 Verifying token...');

    try {
      const response = await axios.post(
        'https://n8n.iniai.cloud/webhook/verify-token',
        {},
        {
          withCredentials: true,
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('🔍 Webhook Response:', response.data);

      if (response.data[0]?.valid === "true") { 
        console.log('✅ Token valid. User authenticated.');
        
        const userData = response.data[0].user;
        localStorage.setItem("authUser", JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        console.warn('⚠️ Token verification failed. Attempting refresh...');
        
        // Try to refresh token instead of immediately logging out
        const refreshResult = await refreshToken();
        if (!refreshResult.success) {
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem("authUser");
        }
      }
    } catch (error) {
      console.error('❌ Token verification error:', error);
      
      // Try refresh as fallback
      const refreshResult = await refreshToken();
      if (!refreshResult.success) {
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem("authUser");
      }
    } finally {
      setIsLoading(false);
    }
  }, [refreshToken]);

  useEffect(() => {
    const init = async () => {
      console.log('🚀 Starting authentication check...');
      
      // Restore user from localStorage
      const storedUser = localStorage.getItem("authUser");
      if (storedUser) {
        console.log("🔄 Restoring user from localStorage...");
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      }

      // Verify token with backend
      await verifyToken();

      // Set up auto-refresh every 50 minutes (before the 1 hour expiry)
      const refreshInterval = setInterval(() => {
        console.log('⏰ Auto-refreshing token...');
        refreshToken();
      }, 50 * 60 * 1000);

      return () => {
        console.log("🛑 Cleaning up token refresh interval...");
        clearInterval(refreshInterval);
      };
    };

    init().catch(error => {
      console.error('⚠️ Init failed:', error);
      setIsLoading(false);
    });
  }, [verifyToken, refreshToken]);

  const login = async (email, password) => {
    try {
      console.log(`🔐 Attempting login for ${email}...`);
      const response = await axios.post(
        'https://n8n.iniai.cloud/webhook/login',
        { email, password },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );

      if (response.data[0]?.success === 'true' || response.data[0]?.success === true) {
        console.log('✅ Login successful.');
        const userData = response.data[0].user;
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem("authUser", JSON.stringify(userData));
        return { success: true };
      } else {
        console.warn('⚠️ Login failed:', response.data.message);
        return { success: false, error: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const updateUser = async (userData) => {
    try {
      console.log('🔄 Updating user data...');
      const response = await axios.post(
        'https://n8n.iniai.cloud/webhook/update-user',
        userData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY
          }
        }
      );

      if (response.data[0]?.success === 'true' || response.data[0]?.success === true) {
        console.log('✅ User updated successfully.');
        const updatedUser = response.data[0].user;
        setUser(updatedUser);
        localStorage.setItem("authUser", JSON.stringify(updatedUser));
        return { success: true, user: updatedUser };
      } else {
        console.warn('⚠️ User update failed:', response.data.message);
        return { success: false, error: response.data.message || 'Update failed' };
      }
    } catch (error) {
      console.error('❌ User update error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Update failed'
      };
    }
  };

  const logout = () => {
    console.log('🚪 Logging out...');
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("authUser");
    document.cookie = "auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.iniai.cloud;";
  };

  if (isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      login, 
      logout, 
      refreshToken,
      updateUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};